import React, { useState } from 'react';
import {
  Briefcase,
  Calendar,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Download,
  ExternalLink,
  FileText,
  Loader,
  Mail,
  MapPin,
  Phone,
  Plus,
  X,
} from 'lucide-react';

export default function CandidateFullPanel({
  candidate,
  jobs,
  stages,
  screeningResponse,
  candidateIntelligence,
  noteDraft,
  onNoteDraftChange,
  isSavingNote,
  onAddNote,
  onStageChange,
  onDeleteCandidate,
  onOpenScreening,
  onReassignJob,
  onRequestJobMatches,
  jobMatchResults,
  onResolveCandidateResumeUrl,
  onClose,
}) {
  const [tab, setTab] = React.useState('overview');
  const [showAllMatches, setShowAllMatches] = React.useState(false);

  if (!candidate) return null;

  const stage = stages.find((s) => s.id === candidate.status) || stages[0];
  const fitScore = Number(candidate.rating || 0);
  const fitColor = fitScore >= 75 ? '#34d399' : fitScore >= 50 ? '#fbbf24' : '#f87171';
  const initials = (candidate.name || '??')
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const daysInPipeline = (() => {
    const ms = new Date(candidate.submittedAt || 0).getTime();
    if (!ms) return null;
    return Math.floor((Date.now() - ms) / 86400000);
  })();

  const sortedTimeline = Array.isArray(candidate.crmTimeline)
    ? [...candidate.crmTimeline].sort(
        (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      )
    : [];

  const [cvLoadError, setCvLoadError] = useState(false);

  const candidateNotes = Array.isArray(candidate.candidateNotes)
    ? candidate.candidateNotes
    : [];

  const screeningQs = Array.isArray(candidate.screeningQuestions)
    ? candidate.screeningQuestions
    : Array.isArray(screeningResponse?.questions)
    ? screeningResponse.questions
    : [];

  const candidateCvUrl =
    candidate?.resumeUrl ||
    candidate?.resume_url ||
    candidate?.cvPdfUrl ||
    candidate?.cvUrl ||
    candidate?.resume?.url ||
    candidate?.applicant?.resumeUrl ||
    candidate?.raw?.resume?.url ||
    candidate?.raw?.applicant?.resumeUrl ||
    '';
  const [resolvedCvUrl, setResolvedCvUrl] = useState('');
  const [isResolvingCv, setIsResolvingCv] = useState(Boolean(candidateCvUrl && onResolveCandidateResumeUrl));

  const matchedJob = (jobs || []).find((j) => j.id === candidate.matchedJobId);
  const intelligence = candidateIntelligence || candidate.latestMatchEvidence || null;
  const parsedProfile = candidate.parsedProfile || intelligence?.profile || {};

  React.useEffect(() => {
    let cancelled = false;

    if (!candidateCvUrl) {
      setResolvedCvUrl('');
      setIsResolvingCv(false);
      return undefined;
    }

    if (typeof onResolveCandidateResumeUrl !== 'function') {
      setResolvedCvUrl(candidateCvUrl);
      setIsResolvingCv(false);
      return undefined;
    }

    setIsResolvingCv(true);
    setResolvedCvUrl('');
    onResolveCandidateResumeUrl(candidate)
      .then((url) => {
        if (!cancelled) setResolvedCvUrl(url);
      })
      .catch((error) => {
        console.error('Failed to resolve candidate CV URL', error);
        if (!cancelled) setResolvedCvUrl(candidateCvUrl);
      })
      .finally(() => {
        if (!cancelled) setIsResolvingCv(false);
      });

    return () => {
      cancelled = true;
    };
  }, [candidate, candidateCvUrl, onResolveCandidateResumeUrl]);

  const readableCvUrl = resolvedCvUrl || candidateCvUrl;

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'intelligence', label: 'Intelligence' },
    {
      id: 'notes',
      label: `Notes${candidateNotes.length ? ` (${candidateNotes.length})` : ''}`,
    },
    { id: 'timeline', label: 'Timeline' },
    { id: 'screening', label: 'Screening' },
    { id: 'cv', label: 'CV' },
  ];

  const stageBadgeClass = {
    hired: 'border-emerald-400/40 bg-emerald-500/10 text-emerald-300',
    offer: 'border-green-400/40 bg-green-500/10 text-green-300',
    interview: 'border-yellow-400/40 bg-yellow-500/10 text-yellow-300',
    screening: 'border-violet-400/40 bg-violet-500/10 text-violet-300',
    applied: 'border-blue-400/40 bg-blue-500/10 text-blue-300',
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-slate-950">
      {/* ── Top bar ── */}
      <div className="flex shrink-0 items-center justify-between border-b border-white/10 bg-slate-950/95 px-6 py-4 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-2 rounded-ctl border border-white/10 bg-white/5 px-3 py-2 text-meta font-black uppercase tracking-[0.14em] text-slate-300 transition hover:bg-white/10 hover:text-white"
          >
            <X size={13} /> Close
          </button>
          <div className="hidden h-4 w-px bg-white/10 sm:block" />
          <div className="hidden text-xs text-slate-500 sm:block">
            Pipeline &rsaquo; {stage.label}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Stage quick-change */}
          <div className="relative">
            <select
              value={candidate.status}
              onChange={(e) => onStageChange(candidate.id, e.target.value)}
              className="appearance-none cursor-pointer rounded-ctl border border-white/10 bg-white/5 py-2 pl-3 pr-8 text-meta font-black uppercase tracking-[0.12em] text-white transition hover:border-cyan-400/40 focus:outline-none"
            >
              {stages.map((s) => (
                <option key={s.id} value={s.id} className="bg-slate-900 text-white">
                  {s.label}
                </option>
              ))}
            </select>
            <ChevronDown
              size={12}
              className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
          </div>

          <button
            type="button"
            onClick={() => {
              if (window.confirm(`Remove ${candidate.name} from the pipeline?`)) {
                onDeleteCandidate(candidate.id);
                onClose();
              }
            }}
            className="rounded-ctl border border-red-400/20 bg-red-500/10 px-3 py-2 text-meta font-black uppercase tracking-[0.14em] text-red-300 transition hover:bg-red-500/20"
          >
            Delete
          </button>
        </div>
      </div>

      {/* ── Hero ── */}
      <div className="shrink-0 border-b border-white/10 bg-gradient-to-b from-slate-900 to-slate-950 px-8 py-6">
        <div className="mx-auto flex max-w-6xl items-start gap-6">
          {/* Avatar + fit score ring */}
          <div className="relative shrink-0">
            <svg width="72" height="72" viewBox="0 0 72 72">
              <circle cx="36" cy="36" r="32" fill="none" stroke="#1e293b" strokeWidth="6" />
              <circle
                cx="36"
                cy="36"
                r="32"
                fill="none"
                stroke={fitColor}
                strokeWidth="6"
                strokeDasharray={`${2 * Math.PI * 32}`}
                strokeDashoffset={`${2 * Math.PI * 32 * (1 - fitScore / 100)}`}
                strokeLinecap="round"
                transform="rotate(-90 36 36)"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-800 text-base font-black text-white">
                {initials}
              </div>
            </div>
            <div
              className="absolute -bottom-1 -right-1 rounded-full border border-slate-800 px-1.5 py-0.5 text-micro font-black"
              style={{ background: fitColor, color: '#0f172a' }}
            >
              {fitScore}%
            </div>
          </div>

          {/* Name + meta */}
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-black text-white sm:text-3xl">{candidate.name}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-3 text-sm text-slate-400">
              {candidate.role && <span>{candidate.role}</span>}
              {candidate.company && (
                <>
                  <span className="text-slate-600">·</span>
                  <span>{candidate.company}</span>
                </>
              )}
              {candidate.location && (
                <>
                  <span className="text-slate-600">·</span>
                  <span className="flex items-center gap-1">
                    <MapPin size={11} />
                    {candidate.location}
                  </span>
                </>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-micro font-black uppercase tracking-[0.14em] ${stageBadgeClass[stage.id] || stageBadgeClass.applied}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${stage.color}`} />
                {stage.label}
              </span>
              {daysInPipeline !== null && (
                <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-micro font-black uppercase tracking-[0.14em] text-slate-400">
                  <Clock3 size={10} /> {daysInPipeline}d in pipeline
                </span>
              )}
              {candidate.source && (
                <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-micro font-black uppercase tracking-[0.14em] text-slate-500">
                  {candidate.source}
                </span>
              )}
            </div>
          </div>

          {/* Quick contact strip (desktop) */}
          <div className="hidden shrink-0 flex-col gap-2 lg:flex">
            {candidate.email && (
              <a
                href={`mailto:${candidate.email}`}
                className="flex items-center gap-2 rounded-ctl border border-white/10 bg-white/5 px-4 py-2.5 text-meta text-slate-300 transition hover:border-cyan-400/30 hover:text-cyan-300"
              >
                <Mail size={13} /> {candidate.email}
              </a>
            )}
            {candidate.phone && (
              <a
                href={`tel:${candidate.phone}`}
                className="flex items-center gap-2 rounded-ctl border border-white/10 bg-white/5 px-4 py-2.5 text-meta text-slate-300 transition hover:border-cyan-400/30 hover:text-cyan-300"
              >
                <Phone size={13} /> {candidate.phone}
              </a>
            )}
            {candidate.linkedinUrl && (
              <a
                href={candidate.linkedinUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 rounded-ctl border border-white/10 bg-white/5 px-4 py-2.5 text-meta text-slate-300 transition hover:border-cyan-400/30 hover:text-cyan-300"
              >
                <ExternalLink size={13} /> LinkedIn
              </a>
            )}
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="shrink-0 border-b border-white/10 px-8">
        <div className="mx-auto flex max-w-6xl gap-0">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`border-b-2 px-5 py-3.5 text-meta font-black uppercase tracking-[0.14em] transition ${
                tab === t.id
                  ? 'border-cyan-400 text-cyan-300'
                  : 'border-transparent text-slate-500 hover:text-slate-300'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab content ── */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        <div className="mx-auto max-w-6xl">

          {/* Overview */}
          {tab === 'overview' && (
            <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
              <div className="space-y-5">

                {/* Contact */}
                <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                  <div className="mb-4 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Contact</div>
                  <div className="space-y-3">
                    {candidate.email && (
                      <div className="flex items-center gap-3">
                        <Mail size={14} className="shrink-0 text-slate-500" />
                        <a href={`mailto:${candidate.email}`} className="text-sm text-cyan-300 hover:underline">
                          {candidate.email}
                        </a>
                        <button
                          type="button"
                          onClick={() => navigator.clipboard.writeText(candidate.email)}
                          className="ml-auto text-micro text-slate-600 hover:text-slate-400"
                        >
                          Copy
                        </button>
                      </div>
                    )}
                    {candidate.phone && (
                      <div className="flex items-center gap-3">
                        <Phone size={14} className="shrink-0 text-slate-500" />
                        <a href={`tel:${candidate.phone}`} className="text-sm text-slate-300">
                          {candidate.phone}
                        </a>
                      </div>
                    )}
                    {candidate.location && (
                      <div className="flex items-center gap-3">
                        <MapPin size={14} className="shrink-0 text-slate-500" />
                        <span className="text-sm text-slate-300">{candidate.location}</span>
                      </div>
                    )}
                    {candidate.linkedinUrl && (
                      <div className="flex items-center gap-3">
                        <ExternalLink size={14} className="shrink-0 text-slate-500" />
                        <a
                          href={candidate.linkedinUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm text-cyan-300 hover:underline"
                        >
                          LinkedIn Profile
                        </a>
                      </div>
                    )}
                    {!candidate.email && !candidate.phone && !candidate.location && (
                      <div className="text-sm text-slate-600">No contact details on record.</div>
                    )}
                  </div>
                </div>

                {/* Commercial asset profile */}
                <div className="rounded-card border border-cyan-400/15 bg-cyan-500/[0.04] p-5">
                  <div className="mb-4 text-micro font-black uppercase tracking-[0.2em] text-cyan-300">Commercial Asset Profile</div>
                  <div className="grid gap-3 sm:grid-cols-4">
                    {[
                      ['Owner', candidate.commercialProfile?.ownerName || candidate.ownerRecruiterName || 'Unassigned'],
                      ['Marketability', `${candidate.commercialProfile?.marketabilityScore || candidate.rating || 0}/100`],
                      ['Reusable', `${candidate.commercialProfile?.reusableScore || 0}/100`],
                      ['Revenue', Number(candidate.commercialProfile?.lifetimeRevenue || candidate.placementFee || 0).toLocaleString()],
                    ].map(([label, value]) => (
                      <div key={label} className="rounded-ctl border border-white/10 bg-slate-950/60 px-3 py-3">
                        <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">{label}</div>
                        <div className="mt-1 truncate text-sm font-black text-white">{value}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-ctl border border-white/10 bg-white/[0.03] px-3 py-3">
                      <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Applications</div>
                      <div className="mt-1 text-lg font-black text-white">{candidate.commercialProfile?.lifetimeApplications || (candidate.matchedJobId ? 1 : 0)}</div>
                    </div>
                    <div className="rounded-ctl border border-white/10 bg-white/[0.03] px-3 py-3">
                      <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Client submissions</div>
                      <div className="mt-1 text-lg font-black text-white">{candidate.commercialProfile?.lifetimeSubmissions || candidate.submittals?.length || 0}</div>
                    </div>
                    <div className="rounded-ctl border border-white/10 bg-white/[0.03] px-3 py-3">
                      <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Relationship</div>
                      <div className="mt-1 text-sm font-black text-white">{candidate.commercialProfile?.relationshipStatus || candidate.relationshipStatus || 'Untouched'}</div>
                    </div>
                  </div>
                </div>

                {/* Role match */}
                <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                  <div className="mb-4 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Role Match</div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Briefcase size={13} className="shrink-0 text-slate-500" />
                      <span className="text-sm font-bold text-white">
                        {candidate.matchedJobTitle || candidate.role || '—'}
                      </span>
                    </div>
                    {matchedJob && (
                      <div className="text-xs text-slate-400">
                        {matchedJob.companyName || matchedJob.company || 'Company'} —{' '}
                        {matchedJob.location || '—'}
                      </div>
                    )}
                    {candidate.yearsExperience != null && (
                      <div className="text-xs text-slate-500">
                        {candidate.yearsExperience} years experience
                      </div>
                    )}
                  </div>

                  {/* Additional job matches from intake */}
                  {!showAllMatches && Array.isArray(candidate.additionalJobMatches) && candidate.additionalJobMatches.length > 0 && (
                    <div className="mt-4 border-t border-white/5 pt-4">
                      <div className="mb-2 text-micro font-black uppercase tracking-[0.16em] text-slate-500">Also a strong fit for</div>
                      <div className="space-y-1.5">
                        {candidate.additionalJobMatches.map((match) => (
                          <div key={match.jobId} className="flex items-center gap-2 rounded-ctl border border-white/5 bg-slate-800/60 px-3 py-2">
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-meta font-bold text-slate-200">{match.jobTitle}</div>
                              {match.companyName && <div className="truncate text-micro text-slate-500">{match.companyName}</div>}
                            </div>
                            <span className={`shrink-0 rounded-full px-2 py-0.5 text-micro font-black ${match.score >= 70 ? 'bg-emerald-500/20 text-emerald-300' : match.score >= 45 ? 'bg-amber-500/20 text-amber-300' : 'bg-slate-700/60 text-slate-400'}`}>{match.score}</span>
                            {typeof onReassignJob === 'function' && (
                              <button
                                type="button"
                                onClick={() => onReassignJob(candidate.id, match.jobId)}
                                className="shrink-0 rounded-full border border-violet-400/25 bg-violet-500/10 px-2.5 py-1 text-micro font-black uppercase tracking-[0.12em] text-violet-300 transition hover:bg-violet-500/20"
                              >
                                Make primary
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Score against all open roles — on demand */}
                  <div className="mt-4 border-t border-white/5 pt-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">
                        {showAllMatches ? 'All open roles' : 'Vacancy fit analysis'}
                      </div>
                      <div className="flex gap-1.5">
                        {typeof onRequestJobMatches === 'function' && (
                          <button
                            type="button"
                            onClick={() => { onRequestJobMatches(candidate.id); setShowAllMatches(true); }}
                            className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-2.5 py-1 text-micro font-black uppercase tracking-[0.12em] text-cyan-300 transition hover:bg-cyan-500/20"
                          >
                            {jobMatchResults ? 'Refresh' : 'Score against all roles'}
                          </button>
                        )}
                        {showAllMatches && jobMatchResults && (
                          <button
                            type="button"
                            onClick={() => setShowAllMatches(false)}
                            className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-micro font-black uppercase tracking-[0.12em] text-slate-400 transition hover:text-white"
                          >
                            Hide
                          </button>
                        )}
                      </div>
                    </div>

                    {showAllMatches && jobMatchResults && (
                      <div className="mt-3 space-y-2">
                        {jobMatchResults.length === 0 && (
                          <div className="text-meta text-slate-500">No active vacancies to score against.</div>
                        )}
                        {jobMatchResults.map((match) => {
                          const tier = match.blendedScore >= 75 ? 'strong' : match.blendedScore >= 62 ? 'moderate' : match.blendedScore >= 42 ? 'weak' : 'no-fit';
                          const scoreClass = tier === 'strong' ? 'bg-emerald-500/20 text-emerald-300' : tier === 'moderate' ? 'bg-amber-500/20 text-amber-300' : tier === 'weak' ? 'bg-orange-500/20 text-orange-300' : 'bg-rose-500/15 text-rose-400';
                          const borderClass = tier === 'strong' ? 'border-emerald-400/15' : tier === 'moderate' ? 'border-amber-400/15' : tier === 'weak' ? 'border-orange-400/10' : 'border-rose-400/10';
                          return (
                            <div key={match.jobId} className={`rounded-ctl border bg-slate-900/60 px-3 py-2.5 ${borderClass}`}>
                              <div className="flex items-start gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5">
                                    <span className="truncate text-meta font-black text-white">{match.jobTitle}</span>
                                    {match.isPrimary && <span className="shrink-0 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-1.5 py-0.5 text-micro font-black uppercase tracking-[0.12em] text-cyan-300">Primary</span>}
                                  </div>
                                  {match.companyName && <div className="mt-0.5 truncate text-micro text-slate-500">{match.companyName}</div>}
                                  <div className="mt-1.5 text-micro leading-4 text-slate-400">{match.reason}</div>
                                </div>
                                <div className="flex shrink-0 flex-col items-end gap-1.5">
                                  <span className={`rounded-full px-2 py-0.5 text-micro font-black ${scoreClass}`}>{match.blendedScore}</span>
                                  {!match.isPrimary && typeof onReassignJob === 'function' && match.blendedScore >= 42 && (
                                    <button
                                      type="button"
                                      onClick={() => onReassignJob(candidate.id, match.jobId)}
                                      className="rounded-full border border-violet-400/20 bg-violet-500/10 px-2 py-0.5 text-micro font-black uppercase tracking-[0.1em] text-violet-300 transition hover:bg-violet-500/20"
                                    >
                                      Make primary
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Skills */}
                {Array.isArray(candidate.skills) && candidate.skills.length > 0 && (
                  <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                    <div className="mb-4 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Skills</div>
                    <div className="flex flex-wrap gap-2">
                      {candidate.skills.map((skill, i) => (
                        <span
                          key={i}
                          className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1 text-meta font-semibold text-cyan-300"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Offer (quick glance on Overview) */}
                {candidate.offer && (() => {
                  const o = candidate.offer;
                  const isExpired = o.status === 'pending' && o.expiryDate && new Date(o.expiryDate) < new Date();
                  const effectiveStatus = isExpired ? 'expired' : (o.status || 'pending');
                  const offerStatusConfig = {
                    pending: { label: 'Offer Pending', cls: 'border-emerald-400/20 bg-emerald-500/5', labelCls: 'text-emerald-400', badgeCls: 'border-emerald-400/30 bg-emerald-500/10 text-emerald-300' },
                    accepted: { label: 'Offer Accepted', cls: 'border-emerald-400/30 bg-emerald-500/10', labelCls: 'text-emerald-300', badgeCls: 'border-emerald-400/30 bg-emerald-500/15 text-emerald-200' },
                    declined: { label: 'Offer Declined', cls: 'border-rose-400/20 bg-rose-500/5', labelCls: 'text-rose-400', badgeCls: 'border-rose-400/30 bg-rose-500/10 text-rose-300' },
                    expired: { label: 'Offer Expired', cls: 'border-amber-400/20 bg-amber-500/5', labelCls: 'text-amber-400', badgeCls: 'border-amber-400/30 bg-amber-500/10 text-amber-300' },
                  };
                  const osc = offerStatusConfig[effectiveStatus] || offerStatusConfig.pending;
                  return (
                    <div className={`rounded-card border p-5 ${osc.cls}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className={`text-micro font-black uppercase tracking-[0.2em] ${osc.labelCls}`}>Offer</div>
                        <span className={`text-micro font-black uppercase tracking-[0.14em] rounded-full border px-2.5 py-1 ${osc.badgeCls}`}>{osc.label.replace('Offer ', '')}</span>
                      </div>
                      <div className="flex flex-wrap gap-4">
                        {o.amount && <div><div className="text-micro text-slate-500 uppercase tracking-[0.14em]">Amount</div><div className="text-lg font-black text-white">{o.currency || 'USD'} {Number(o.amount).toLocaleString()}</div></div>}
                        {o.expiryDate && <div><div className="text-micro text-slate-500 uppercase tracking-[0.14em]">Expires</div><div className={`text-sm font-bold ${isExpired ? 'text-amber-300' : 'text-white'}`}>{new Date(o.expiryDate).toLocaleDateString()}</div></div>}
                        {o.acceptedAt && <div><div className="text-micro text-slate-500 uppercase tracking-[0.14em]">Accepted</div><div className="text-sm font-bold text-emerald-300">{new Date(o.acceptedAt).toLocaleDateString()}</div></div>}
                      </div>
                    </div>
                  );
                })()}

                {/* Interview */}
                {(candidate.interviewDate || candidate.interviewType || candidate.interviewerName) && (() => {
                  const isPast = candidate.interviewDate && new Date(candidate.interviewDate) < new Date();
                  return (
                    <div className="rounded-card border border-amber-400/20 bg-amber-500/5 p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-micro font-black uppercase tracking-[0.2em] text-amber-500">Interview</div>
                        {candidate.interviewDate && (
                          <span className={`text-micro font-black uppercase tracking-[0.14em] rounded-full border px-2.5 py-1 ${
                            isPast ? 'border-slate-700 bg-slate-800 text-slate-400' : 'border-amber-400/30 bg-amber-500/10 text-amber-300'
                          }`}>{isPast ? 'Completed' : 'Upcoming'}</span>
                        )}
                      </div>
                      <div className="space-y-2">
                        {candidate.interviewDate && (
                          <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                            <Calendar size={13} className="shrink-0 text-amber-500" />
                            {new Date(candidate.interviewDate).toLocaleString([], { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                        {candidate.interviewType && (
                          <div className="text-xs text-slate-400">Format: {candidate.interviewType}</div>
                        )}
                        {candidate.interviewerName && (
                          <div className="text-xs text-slate-400">Interviewer: {candidate.interviewerName}</div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Placement (hired) */}
                {(candidate.offerSalary || candidate.placementFee || candidate.startDate) && (
                  <div className="rounded-card border border-violet-400/20 bg-violet-500/5 p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-micro font-black uppercase tracking-[0.2em] text-violet-400">Placement</div>
                      <span className="flex items-center gap-1.5 rounded-full border border-violet-400/30 bg-violet-500/10 px-2.5 py-1 text-micro font-black uppercase tracking-[0.14em] text-violet-300">
                        <CheckCircle2 size={10} /> Placed
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                      {candidate.offerSalary && (
                        <div>
                          <div className="mb-1 text-micro uppercase tracking-[0.16em] text-slate-500">Salary</div>
                          <div className="text-sm font-bold text-white">{Number(candidate.offerSalary).toLocaleString()}</div>
                        </div>
                      )}
                      {candidate.placementFeePercent && (
                        <div>
                          <div className="mb-1 text-micro uppercase tracking-[0.16em] text-slate-500">Agency Fee</div>
                          <div className="text-sm font-bold text-white">{candidate.placementFeePercent}%</div>
                        </div>
                      )}
                      {candidate.placementFee && (
                        <div>
                          <div className="mb-1 text-micro uppercase tracking-[0.16em] text-slate-500">Fee Earned</div>
                          <div className="text-sm font-bold text-violet-300">{Number(candidate.placementFee).toLocaleString()}</div>
                        </div>
                      )}
                      {candidate.startDate && (
                        <div>
                          <div className="mb-1 text-micro uppercase tracking-[0.16em] text-slate-500">Start Date</div>
                          <div className="text-sm font-bold text-white">{new Date(candidate.startDate).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Right sidebar */}
              <div className="space-y-5">
                {/* Stage history */}
                <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                  <div className="mb-4 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Stage History</div>
                  {Array.isArray(candidate.stageHistory) && candidate.stageHistory.length > 0 ? (
                    <div className="space-y-2">
                      {[...candidate.stageHistory].reverse().map((evt, i) => {
                        const s = stages.find((st) => st.id === evt.stageId);
                        return (
                          <div key={i} className="flex items-center gap-2.5">
                            <div className={`h-2 w-2 shrink-0 rounded-full ${s?.color || 'bg-slate-500'}`} />
                            <div className="flex-1 text-xs font-semibold text-slate-300">
                              {evt.stageLabel || evt.stageId}
                            </div>
                            <div className="text-micro text-slate-600">
                              {evt.movedAt ? new Date(evt.movedAt).toLocaleDateString() : ''}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-xs text-slate-600">No stage history recorded.</div>
                  )}
                </div>

                {/* Offer decline */}
                {candidate.offerDeclinedAt && (
                  <div className="rounded-card border border-rose-400/20 bg-rose-500/5 p-5">
                    <div className="mb-2 text-micro font-black uppercase tracking-[0.2em] text-rose-400">Offer Declined</div>
                    {candidate.offerDeclineCategory && (
                      <div className="text-xs font-semibold text-slate-300">
                        {candidate.offerDeclineCategory.replace(/_/g, ' ')}
                      </div>
                    )}
                    {candidate.offerDeclineNote && (
                      <div className="mt-1 text-xs text-slate-500">{candidate.offerDeclineNote}</div>
                    )}
                    <div className="mt-2 text-micro text-slate-600">
                      {new Date(candidate.offerDeclinedAt).toLocaleDateString()}
                    </div>
                  </div>
                )}

                {/* QoH */}
                {(candidate.qohScore90 || candidate.qohScore180) && (
                  <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                    <div className="mb-3 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Quality of Hire</div>
                    <div className="flex gap-4">
                      {candidate.qohScore90 && (
                        <div className="text-center">
                          <div className="text-xl font-black text-cyan-300">{candidate.qohScore90}</div>
                          <div className="text-micro text-slate-500">90-day</div>
                        </div>
                      )}
                      {candidate.qohScore180 && (
                        <div className="text-center">
                          <div className="text-xl font-black text-cyan-300">{candidate.qohScore180}</div>
                          <div className="text-micro text-slate-500">180-day</div>
                        </div>
                      )}
                    </div>
                    {candidate.qohNotes && (
                      <div className="mt-2 text-xs text-slate-400">{candidate.qohNotes}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === 'intelligence' && (
            <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
              <div className="space-y-5">
                <div className="rounded-card border border-cyan-400/15 bg-cyan-500/[0.04] p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-micro font-black uppercase tracking-[0.2em] text-cyan-300">Parsed Candidate Intelligence</div>
                      <div className="mt-2 text-sm leading-6 text-slate-300">
                        Vision converts this CV, notes, screening, and job history into recruiter-searchable evidence.
                      </div>
                    </div>
                    <div className="rounded-full border border-cyan-400/25 bg-cyan-500/10 px-3 py-1 text-micro font-black text-cyan-100">
                      Parse {parsedProfile.parseabilityScore || intelligence?.dimensions?.parseability || 0}/100
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3 sm:grid-cols-4">
                    {[
                      ['Seniority', parsedProfile.seniority || intelligence?.profile?.seniority || 'Unknown'],
                      ['Years', parsedProfile.yearsExperience || intelligence?.profile?.yearsExperience || 'n/a'],
                      ['Scope', parsedProfile.scopeLabel || intelligence?.profile?.scope?.label || 'Unclear'],
                      ['Match', intelligence?.overall ? `${intelligence.overall}/99` : `${candidate.rating || 0}/100`],
                    ].map(([label, value]) => (
                      <div key={label} className="rounded-ctl border border-white/10 bg-slate-950/60 px-4 py-3">
                        <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-500">{label}</div>
                        <div className="mt-1 text-sm font-black text-white">{value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {intelligence?.dimensions && (
                  <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                    <div className="mb-4 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Explainable match breakdown</div>
                    <div className="space-y-3">
                      {Object.entries(intelligence.dimensions).map(([key, value]) => (
                        <div key={key}>
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-bold capitalize text-slate-300">{key}</span>
                            <span className="font-black text-white">{value}</span>
                          </div>
                          <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-800">
                            <div className="h-full rounded-full bg-cyan-400" style={{ width: `${Math.max(4, Math.min(100, Number(value || 0)))}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                  <div className="mb-4 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Evidence and gaps</div>
                  <div className="grid gap-3 lg:grid-cols-2">
                    <div className="rounded-ctl border border-emerald-400/15 bg-emerald-500/10 p-4">
                      <div className="text-micro font-black uppercase tracking-[0.14em] text-emerald-300">Evidence</div>
                      <div className="mt-2 space-y-2">
                        {(intelligence?.evidence || []).length === 0 && <div className="text-xs text-slate-500">No evidence extracted yet.</div>}
                        {(intelligence?.evidence || []).slice(0, 3).map((item, index) => (
                          <div key={index} className="text-xs leading-5 text-slate-200">{item}</div>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-ctl border border-amber-400/15 bg-amber-500/10 p-4">
                      <div className="text-micro font-black uppercase tracking-[0.14em] text-amber-300">Recruiter fixes</div>
                      <div className="mt-2 space-y-2">
                        {((parsedProfile.scopeNeeds || intelligence?.profile?.scope?.needs || intelligence?.missing || [])).slice(0, 4).map((item, index) => (
                          <div key={index} className="text-xs leading-5 text-slate-200">{item}</div>
                        ))}
                        {!(parsedProfile.scopeNeeds || intelligence?.profile?.scope?.needs || intelligence?.missing || []).length && <div className="text-xs text-slate-500">No major gaps detected.</div>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-card border border-white/10 bg-slate-900/50 p-4">
                  <div className="mb-3 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Skills</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(parsedProfile.skills || intelligence?.profile?.skills || candidate.skills || []).slice(0, 18).map((skill) => (
                      <span key={skill} className="rounded-full border border-cyan-400/15 bg-cyan-500/10 px-2 py-0.5 text-micro font-bold text-cyan-200">{skill}</span>
                    ))}
                  </div>
                </div>
                <div className="rounded-card border border-white/10 bg-slate-900/50 p-4">
                  <div className="mb-2 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Next action</div>
                  <div className="text-sm leading-6 text-slate-200">{intelligence?.nextAction || 'Keep this candidate warm and complete missing profile fields.'}</div>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          {tab === 'notes' && (
            <div className="max-w-2xl space-y-5">
              <div className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                <div className="mb-3 text-micro font-black uppercase tracking-[0.2em] text-slate-500">Add Note</div>
                <textarea
                  value={noteDraft}
                  onChange={(e) => onNoteDraftChange(e.target.value)}
                  placeholder="Type a recruiter note…"
                  rows={4}
                  className="w-full resize-none rounded-ctl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-white placeholder-slate-600 focus:border-cyan-500/50 focus:outline-none"
                />
                <button
                  type="button"
                  disabled={!noteDraft.trim() || isSavingNote}
                  onClick={() => onAddNote(candidate.id, noteDraft)}
                  className="mt-3 flex items-center gap-2 rounded-ctl bg-cyan-500 px-5 py-2.5 text-meta font-black uppercase tracking-[0.14em] text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {isSavingNote ? (
                    <Loader size={12} className="animate-spin" />
                  ) : (
                    <Plus size={12} />
                  )}
                  {isSavingNote ? 'Saving…' : 'Save Note'}
                </button>
              </div>

              {candidateNotes.length === 0 ? (
                <div className="rounded-card border border-dashed border-white/10 px-5 py-8 text-center text-sm text-slate-600">
                  No notes yet. Add the first one above.
                </div>
              ) : (
                <div className="space-y-3">
                  {[...candidateNotes].reverse().map((note, i) => (
                    <div key={i} className="rounded-card border border-white/10 bg-slate-900/50 p-5">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="text-meta font-black text-slate-400">
                          {note.authorName || 'Recruiter'}
                        </div>
                        <div className="text-micro text-slate-600">
                          {note.createdAt ? new Date(note.createdAt).toLocaleString() : ''}
                        </div>
                      </div>
                      <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-300">
                        {note.text}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Timeline */}
          {tab === 'timeline' && (
            <div className="max-w-2xl">
              {sortedTimeline.length === 0 ? (
                <div className="rounded-card border border-dashed border-white/10 px-5 py-8 text-center text-sm text-slate-600">
                  No timeline events yet.
                </div>
              ) : (
                <div className="relative">
                  <div className="absolute bottom-4 left-[19px] top-4 w-px bg-white/10" />
                  {sortedTimeline.map((evt, i) => (
                    <div key={i} className="relative flex gap-4 py-3">
                      <div className="relative z-10 mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-white/20 bg-slate-800">
                        <div className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
                      </div>
                      <div className="flex-1 rounded-card border border-white/10 bg-slate-900/50 px-4 py-3">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm text-slate-300">{evt.summary || evt.type || 'Event'}</p>
                          <div className="shrink-0 text-micro text-slate-600">
                            {evt.createdAt ? new Date(evt.createdAt).toLocaleString() : ''}
                          </div>
                        </div>
                        {evt.type && (
                          <div className="mt-1.5 inline-block rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-micro font-semibold text-slate-500">
                            {evt.type.replace(/_/g, ' ')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Screening */}
          {tab === 'screening' && (
            <div className="max-w-2xl space-y-4">
              {(candidate.screeningSubmittedAt || candidate.screeningCompletedAt) && (
                <>
                  <div className="rounded-card border border-violet-400/20 bg-violet-500/5 p-4">
                    <div className="text-meta font-semibold text-violet-300">
                      Screening submitted{' '}
                      {candidate.screeningSubmittedAt
                        ? new Date(candidate.screeningSubmittedAt).toLocaleString()
                        : ''}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      onOpenScreening(candidate.id);
                    }}
                    className="flex items-center gap-2 rounded-ctl border border-violet-400/30 bg-violet-500/10 px-5 py-2.5 text-meta font-black uppercase tracking-[0.14em] text-violet-200 transition hover:bg-violet-500/20"
                  >
                    <FileText size={13} /> Open Full Screening Review
                  </button>
                </>
              )}

              {screeningQs.length > 0 ? (
                <div className="space-y-3">
                  {screeningQs.map((q, i) => {
                    const questionText =
                      typeof q === 'string'
                        ? q
                        : q.question || q.text || q.label || `Question ${i + 1}`;
                    const answerText =
                      typeof q === 'object' ? q.answer || q.response || q.value || '' : '';
                    return (
                      <div key={i} className="rounded-card border border-white/10 bg-slate-900/50 p-4">
                        <div className="mb-2 text-meta font-black uppercase tracking-[0.14em] text-slate-500">
                          Q{i + 1}
                        </div>
                        <p className="text-sm font-semibold text-white">{questionText}</p>
                        {answerText && (
                          <p className="mt-2 text-sm leading-relaxed text-slate-400">{answerText}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-card border border-dashed border-white/10 px-5 py-8 text-center text-sm text-slate-600">
                  No screening questions on file for this candidate.
                </div>
              )}
            </div>
          )}

          {/* CV */}
          {tab === 'cv' && (
            <div className="max-w-4xl">
              {candidateCvUrl ? (
                <div className="space-y-4">
                  {isResolvingCv ? (
                    <div className="flex h-12 items-center gap-2 text-sm text-slate-400">
                      <Loader size={16} className="animate-spin" /> Loading secure CV preview…
                    </div>
                  ) : (
                  <div className="flex items-center gap-3">
                    <a
                      href={readableCvUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 rounded-ctl border border-white/10 bg-white/5 px-4 py-2.5 text-meta font-black uppercase tracking-[0.14em] text-slate-200 transition hover:border-white/20 hover:text-white"
                    >
                      <Download size={13} /> Download CV
                    </a>
                    <a
                      href={readableCvUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 rounded-ctl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2.5 text-meta font-black uppercase tracking-[0.14em] text-cyan-300 transition hover:bg-cyan-500/20"
                    >
                      <ExternalLink size={13} /> Open in new tab
                    </a>
                  </div>
                  )}
                  <div
                    className="overflow-hidden rounded-card border border-white/10"
                    style={{ height: 'calc(100vh - 380px)' }}
                  >
                    {isResolvingCv ? (
                      <div className="flex h-full items-center justify-center gap-2 bg-slate-900 text-sm text-slate-400">
                        <Loader size={16} className="animate-spin" /> Loading CV…
                      </div>
                    ) : cvLoadError ? (
                      <div className="flex h-full flex-col items-center justify-center gap-3 bg-slate-900 p-6 text-center">
                        <div className="text-sm text-slate-400">Could not render PDF preview. Try opening in a new tab instead.</div>
                        <a href={readableCvUrl} target="_blank" rel="noreferrer" className="rounded-ctl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-xs font-bold text-cyan-300 hover:bg-cyan-500/20">
                          Open CV in new tab
                        </a>
                      </div>
                    ) : (
                      <iframe
                        src={`${readableCvUrl}${readableCvUrl.includes('?') ? '&' : '?'}alt=media`}
                        title="Candidate CV"
                        className="h-full w-full bg-white"
                        style={{ border: 'none' }}
                        onError={() => {
                          console.warn('CV iframe failed to load:', readableCvUrl);
                          setCvLoadError(true);
                        }}
                        onLoad={() => {
                          console.log('CV iframe loaded successfully:', readableCvUrl);
                          setCvLoadError(false);
                        }}
                      />
                    )}
                  </div>
                </div>
              ) : (
                <div className="rounded-card border border-dashed border-white/10 px-5 py-12 text-center">
                  <FileText size={32} className="mx-auto mb-3 text-slate-700" />
                  <div className="text-sm font-bold text-slate-500">
                    No CV uploaded for this candidate.
                  </div>
                  <div className="mt-1 text-xs text-slate-600">
                    CVs are attached when a candidate is added via upload or applies through the
                    careers page.
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
